// Order PDF Generator & Email Sender
// InMotion Großhandel - Corporate Angebot/Bestellung PDF Template
// Settings-driven: All values come from pdf_settings table

import { base44 } from '@/api/base44Client';
import { toast } from 'sonner';
import {
  fetchPdfSettings as fetchSharedPdfSettings,
  clearPdfSettingsCache as clearSharedPdfSettingsCache,
  downloadPdfFromHtml,
  wrapPdfDocument,
  renderCompanyHeader,
  renderFooter,
  escapeHtml,
} from '@/lib/pdfRenderer';
import { getVorlage, textZuHtml } from '@/lib/dokumentVorlagen';
import { addDays } from 'date-fns';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

/**
 * Fallback defaults (used when no settings loaded)
 */
const DEFAULTS = {
  header_logo_url: '',
  header_left_title: 'I M INMOTION',
  header_left_tagline: 'IMMER IN BEWEGUNG',
  header_right_title: 'GROSSHANDEL | HORECA',
  header_right_subtitle: 'VERPACKUNG | HYGIENE | REINIGUNGSMITTEL',
  company_name: 'InMotion Großhandel UG',
  street: 'Asbach 19',
  zip: '86510',
  city: 'Ried',
  country: 'Deutschland',
  phone: '089 357 650 - 41',
  email: 'info@inmotion-handel.de',
  website: 'www.inmotion-handel.de',
  registry_city: 'Augsburg',
  hr_nr: 'HRB40485',
  steuer_nr: '102/129/30143',
  geschaeftsfuehrer: 'Bünyamin Cicekdag',
  bank_name: 'DEUTSCHE BANK',
  iban: 'DE13 7207 0001 0101 2160 00',
  bic: 'DEUTDEMM720',
  ansprechpartner: 'Buchhaltung',
  greeting_offer: 'vielen Dank für Ihre Anfrage und das Interesse an unseren Leistungen.\nGerne unterbreiten wir Ihnen hiermit folgendes Angebot:',
  greeting_order: 'vielen Dank für Ihre Bestellung. Wir bestätigen folgenden Auftrag:',
  closing_offer: 'Dieses Angebot ist 30 Tage gültig. Wir freuen uns auf Ihren Auftrag.',
  closing_order: 'Vielen Dank für Ihr Vertrauen.',
};

const C = {
  navy: '#172554',
  gold: '#D4A017',
  text: '#1e293b',
  gray: '#6b7280',
  lightGray: '#f3f4f6',
  border: '#d1d5db',
  white: '#ffffff',
};

function eur(val) {
  if (val == null || isNaN(val)) return '0,00 EUR';
  return val.toFixed(2).replace('.', ',') + ' EUR';
}

/**
 * Merge settings with defaults
 */
function mergeSettings(custom) {
  if (!custom) return { ...DEFAULTS };
  const merged = { ...DEFAULTS };
  Object.keys(DEFAULTS).forEach((key) => {
    if (custom[key] != null && custom[key] !== '') {
      merged[key] = custom[key];
    }
  });
  // Dokumentvorlagen (JSON) und weitere Spalten unverändert durchreichen
  Object.keys(custom).forEach((key) => {
    if (!(key in DEFAULTS) && custom[key] != null) merged[key] = custom[key];
  });
  return merged;
}

/**
 * Einstellungen kommen aus lib/pdfRenderer (eine Quelle für ALLE Dokumente).
 * Bei mehreren Zeilen gilt die älteste – identisch mit der Admin-Seite.
 */
async function fetchPdfSettings() {
  try {
    return await fetchSharedPdfSettings();
  } catch {
    return null;
  }
}

/** Cache-Reset (nach dem Speichern der Einstellungen) */
export function clearPdfSettingsCache() {
  clearSharedPdfSettingsCache();
}

/**
 * PDF HTML Template
 * @param {Object} order
 * @param {Object} customSettings - Optional override settings
 */
export function generatePDFTemplate(order, customSettings) {
  const s = mergeSettings(customSettings);
  const isOffer = order.order_type === 'Angebot';
  const isLieferschein = order.order_type === 'Lieferschein';
  const docType = isOffer ? 'Angebot' : (isLieferschein ? 'Lieferschein' : 'Bestellung');
  const docPrefix = isOffer ? 'AN' : (isLieferschein ? 'LI' : 'BS');
  const orderNum = order.order_number || `${docPrefix}-0000`;
  const dateStr = order.created_date
    ? format(new Date(order.created_date), 'dd.MM.yyyy', { locale: de })
    : format(new Date(), 'dd.MM.yyyy', { locale: de });

  const kundenNr = order.lead_id
    ? order.lead_id.substring(0, 4).toUpperCase()
    : '0000';

  // Texte aus der Dokumentvorlage (Admin → PDF-Einstellungen → Texte)
  const vorlage = getVorlage(s, isOffer ? 'angebot' : 'bestellung');
  const createdAt = order.created_date ? new Date(order.created_date) : new Date();
  const gueltigTage = Number(vorlage.gueltigkeit_tage) || 30;
  const ctx = {
    'kunde.name': order.lead_name || '',
    'kunde.nummer': kundenNr,
    'firma.name': s.company_name,
    'firma.geschaeftsfuehrer': s.geschaeftsfuehrer,
    'firma.ansprechpartner': s.ansprechpartner,
    'beleg.nummer': orderNum,
    'beleg.datum': dateStr,
    'bestellung.nummer': orderNum,
    'angebot.gueltig_bis': format(addDays(createdAt, gueltigTage), 'dd.MM.yyyy', { locale: de }),
  };
  const anredeHtml = textZuHtml(vorlage.anrede, ctx);
  const greetingText = isLieferschein
    ? textZuHtml(s.greeting_lieferschein || '', ctx)
    : textZuHtml(vorlage.einleitung, ctx);
  const closingHtml = isLieferschein ? textZuHtml(s.closing_lieferschein || '', ctx) : textZuHtml(vorlage.schluss, ctx);
  const grussHtml = textZuHtml(vorlage.gruss || 'Mit freundlichen Grüßen', ctx);
  const unterzeichnerHtml = textZuHtml(vorlage.unterzeichner || '{{firma.geschaeftsfuehrer}}', ctx);

  // Safely parse items — handles both JSON string, array, and object
  function safeParseItems(items) {
    if (!items) return [];
    if (Array.isArray(items)) return items;
    if (typeof items === 'string') {
      try {
        const parsed = JSON.parse(items);
        if (Array.isArray(parsed)) return parsed;
        if (typeof parsed === 'object' && parsed !== null) return Object.values(parsed);
        return [];
      } catch {
        return [];
      }
    }
    if (typeof items === 'object' && items !== null) {
      return Object.values(items);
    }
    return [];
  }

  const parsedItems = safeParseItems(order.items);

  // Rabatt (%) — en az bir pozisyonda indirim varsa ek kolon gösterilir
  const hasRabatt = parsedItems.some((i) => (Number(i.rabatt_prozent) || 0) > 0);

  // Items
  const itemsHtml = parsedItems.map((item, idx) => {
    const qty = item.quantity || 0;
    const unitPrice = item.unit_price || 0;
    const rabatt = Number(item.rabatt_prozent) || 0;
    const total = qty * unitPrice * (1 - rabatt / 100);
    const unit = item.unit || 'Ktn';
    const desc = item.beschreibung || item.description || '';
    const isZuschlag = item.zeilen_typ === 'mindermengenzuschlag' || item.product_sku === 'MINDERMENGE';
    const imgUrl = isZuschlag ? '' : (item.product_image || item.image_url || '');
    const variant = item.variant || '';
    const lagerort = item.lagerort || '';

    // Product image HTML (compact 24x24 floating left)
    const imgHtml = imgUrl
      ? `<img src="${imgUrl}" alt="" style="width:24px; height:24px; object-fit:cover; border-radius:3px; border:1px solid #e5e7eb; float:left; margin-right:8px; margin-top:1px;">`
      : '';

    // Variant label in Menge column (more compact)
    const variantHtml = variant
      ? `<br><span style="font-size:6.5pt; color:${C.navy}; background:#eef2ff; padding:1px 3px; border-radius:2px; display:inline-block; margin-top:2px;">${variant}</span>`
      : '';

    if (isLieferschein) {
      return `
        <tr>
          <td style="padding:3px 6px; border-bottom:1px solid #e5e7eb; color:${C.gray}; width:28px; vertical-align:top; font-size:8.5pt;">${idx + 1}.</td>
          <td style="padding:3px 6px; border-bottom:1px solid #e5e7eb; vertical-align:top; line-height:1.25;">
            ${imgHtml}
            <div style="overflow:hidden; display:block;">
              <strong style="color:${C.text}; font-size:8.5pt;">${item.product_name || ''}</strong>
              ${desc ? `<br><span style="color:${C.gray}; font-size:6.5pt;">${desc}</span>` : ''}
            </div>
          </td>
          <td style="padding:3px 6px; border-bottom:1px solid #e5e7eb; text-align:right; white-space:nowrap; width:90px; vertical-align:top; font-size:8.5pt;">${qty.toFixed(2).replace('.', ',')} ${unit}${variantHtml}</td>
          <td style="padding:3px 6px; border-bottom:1px solid #e5e7eb; text-align:left; white-space:nowrap; width:120px; font-weight:600; vertical-align:top; font-size:8.5pt;">${lagerort}</td>
        </tr>`;
    } else {
      return `
        <tr>
          <td style="padding:3px 6px; border-bottom:1px solid #e5e7eb; color:${C.gray}; width:28px; vertical-align:top; font-size:8.5pt;">${idx + 1}.</td>
          <td style="padding:3px 6px; border-bottom:1px solid #e5e7eb; vertical-align:top; line-height:1.25;">
            ${imgHtml}
            <div style="overflow:hidden; display:block;">
              <strong style="color:${C.text}; font-size:8.5pt;${isZuschlag ? ' font-style:italic; font-weight:600;' : ''}">${escapeHtml(item.product_name || '')}</strong>
              ${desc && !isZuschlag ? `<br><span style="color:${C.gray}; font-size:6.5pt;">${desc}</span>` : ''}
            </div>
          </td>
          <td style="padding:3px 6px; border-bottom:1px solid #e5e7eb; text-align:right; white-space:nowrap; width:90px; vertical-align:top; font-size:8.5pt;">${isZuschlag ? '1 pauschal' : `${qty.toFixed(2).replace('.', ',')} ${unit}${variantHtml}`}</td>
          <td style="padding:3px 6px; border-bottom:1px solid #e5e7eb; text-align:right; white-space:nowrap; width:80px; vertical-align:top; font-size:8.5pt;">${eur(unitPrice)}</td>
          ${hasRabatt ? `<td style="padding:3px 6px; border-bottom:1px solid #e5e7eb; text-align:right; white-space:nowrap; width:55px; vertical-align:top; font-size:8.5pt; color:${rabatt > 0 ? '#c2410c' : C.gray};">${rabatt > 0 ? rabatt.toString().replace('.', ',') + ' %' : '—'}</td>` : ''}
          <td style="padding:3px 6px; border-bottom:1px solid #e5e7eb; text-align:right; white-space:nowrap; width:85px; font-weight:600; vertical-align:top; font-size:8.5pt;">${eur(total)}</td>
        </tr>`;
    }
  }).join('');

  const subtotal = order.subtotal || parsedItems.reduce(
    (sum, i) => sum + (i.quantity || 0) * (i.unit_price || 0) * (1 - (Number(i.rabatt_prozent) || 0) / 100),
    0
  ) || 0;
  const taxRate = order.tax_rate || 19;
  const taxAmount = order.tax_amount || subtotal * (taxRate / 100);
  const total = order.total || subtotal + taxAmount;

  const body = `
    ${renderCompanyHeader(s)}

    <!-- Address + Doc Info -->
      <div style="display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:8px;">
        <div style="font-size:9pt; line-height:1.4; max-width:55%;">
          <div style="font-weight:600;">${order.lead_name || ''}</div>
          ${order.lead_address || ''}${order.lead_address ? '<br>' : ''}
          ${order.lead_city || ''}${order.lead_plz ? ` ${order.lead_plz}` : ''}${(order.lead_city || order.lead_plz) ? '<br>' : ''}
          ${s.country}
        </div>
        <div style="text-align:right; font-size:8.5pt;">
          <table style="border-collapse:collapse; margin-left:auto;">
            <tr>
              <td style="padding:1px 0; color:${C.gray}; padding-right:12px; text-align:left; font-size:8pt;">${isOffer ? 'Angebots-Nr.' : 'Bestell-Nr.'}</td>
              <td style="padding:1px 0; font-weight:800; text-align:right; font-size:10pt;">${orderNum}</td>
            </tr>
            <tr>
              <td style="padding:1px 0; color:${C.gray}; padding-right:12px; text-align:left; font-size:8pt;">Datum</td>
              <td style="padding:1px 0; font-weight:600; text-align:right; font-size:8.5pt;">${dateStr}</td>
            </tr>
            <tr>
              <td style="padding:1px 0; color:${C.gray}; padding-right:12px; text-align:left; font-size:8pt;">Ihre Kundennummer</td>
              <td style="padding:1px 0; font-weight:600; text-align:right; font-size:8.5pt;">${kundenNr}</td>
            </tr>
            <tr>
              <td style="padding:1px 0; color:${C.gray}; padding-right:12px; text-align:left; font-size:8pt;">Ihr Ansprechpartner</td>
              <td style="padding:1px 0; font-weight:600; text-align:right; font-size:8.5pt;">${s.ansprechpartner}</td>
            </tr>
          </table>
        </div>
      </div>

      <!-- Title -->
      <div style="font-size:11.5pt; font-weight:700; color:${C.navy}; margin-bottom:4px;">${docType} ${orderNum}</div>

      <!-- Anrede + Einleitung (Vorlage) -->
      <div style="font-size:8.5pt; color:${C.text}; line-height:1.4; margin-bottom:6px;">
        ${anredeHtml ? anredeHtml + '<br><br>' : ''}${greetingText}
      </div>

      <!-- Items Table -->
      <table style="width:100%; border-collapse:collapse; margin-bottom:4px;">
        <thead>
          <tr>
            <th style="background:${C.navy}; color:white; font-size:7pt; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; padding:4px 6px; text-align:left;">Pos.</th>
            <th style="background:${C.navy}; color:white; font-size:7pt; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; padding:4px 6px; text-align:left;">Beschreibung</th>
            <th style="background:${C.navy}; color:white; font-size:7pt; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; padding:4px 6px; text-align:right;">Menge</th>
            ${isLieferschein 
              ? `<th style="background:${C.navy}; color:white; font-size:7pt; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; padding:4px 6px; text-align:left;">Lagerort</th>`
              : `<th style="background:${C.navy}; color:white; font-size:7pt; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; padding:4px 6px; text-align:right;">Einzelpreis</th>
                 ${hasRabatt ? `<th style="background:${C.navy}; color:white; font-size:7pt; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; padding:4px 6px; text-align:right;">Rabatt</th>` : ''}
                 <th style="background:${C.navy}; color:white; font-size:7pt; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; padding:4px 6px; text-align:right;">Gesamtpreis</th>`
            }
          </tr>
        </thead>
        <tbody>
          ${itemsHtml}
        </tbody>
      </table>

      <!-- Totals (Hidden for Lieferschein) -->
      ${!isLieferschein ? `
      <div style="width:250px; margin-left:auto; margin-top:8px;">
        <div style="display:flex; justify-content:space-between; padding:3px 0; font-size:9pt; color:${C.text}; border-top:1px solid ${C.border}; padding-top:5px;">
          <span>Nettosumme</span>
          <span>${eur(subtotal)}</span>
        </div>
        <div style="display:flex; justify-content:space-between; padding:3px 0; font-size:9pt; color:${C.text};">
          <span>MwSt. (${taxRate}%)</span>
          <span>${eur(taxAmount)}</span>
        </div>
        <div style="display:flex; justify-content:space-between; border-top:2px solid ${C.navy}; margin-top:3px; padding-top:6px; font-size:11pt; font-weight:700; color:${C.navy};">
          <span>Gesamtbetrag</span>
          <span>${eur(total)}</span>
        </div>
      </div>
      ` : ''}

      ${order.notes ? `
      <div style="margin-top:12px; padding:8px 10px; background:${C.lightGray}; border-radius:3px; font-size:8.5pt;">
        <strong>Anmerkungen:</strong><br>
        ${escapeHtml(order.notes)}
      </div>` : ''}

      <!-- Closing -->
      <div style="margin-top:8px; font-size:8.5pt; line-height:1.4;">
        ${closingHtml ? closingHtml + '<br><br>' : ''}
        ${grussHtml}<br>
        <strong>${unterzeichnerHtml}</strong>
      </div>

      <!-- Signature Field (Only for Lieferschein) -->
      ${isLieferschein ? `
      <div style="margin-top:40px; margin-bottom:20px; font-size:8.5pt;">
        <div style="display:flex; justify-content:space-between; width:100%;">
          <div style="width:45%; border-top:1px solid ${C.border}; padding-top:4px;">
            Datum, Ort
          </div>
          <div style="width:45%; border-top:1px solid ${C.border}; padding-top:4px;">
            Unterschrift
          </div>
        </div>
      </div>
      ` : ''}

    ${renderFooter(s)}
  `;
  return wrapPdfDocument(body, `${docType} ${orderNum}`);
}

/**
 * Generate PDF
 */
export async function generateOrderPDF(order, customSettings) {
  try {
    const settings = customSettings || await fetchPdfSettings();
    const htmlContent = generatePDFTemplate(order, settings);

    try {
      const response = await base44.integrations.Core.GeneratePDF({
        html: htmlContent,
        filename: `${order.order_number}.pdf`,
      });
      return response.pdf_content || response.url;
    } catch {
      return htmlContent;
    }
  } catch (error) {
    console.error('PDF generation error:', error);
    throw error;
  }
}

/**
 * Send order email
 */
export async function sendOrderEmail(order, pdfContent) {
  const settings = await fetchPdfSettings();
  const s = mergeSettings(settings);
  const isOffer = order.order_type === 'Angebot';
  const subject = isOffer
    ? `Ihr Angebot ${order.order_number} von ${s.company_name}`
    : `Ihre Bestellbestätigung ${order.order_number}`;

  const emailBody = `
Sehr geehrte Damen und Herren,

${isOffer
      ? `anbei erhalten Sie unser Angebot ${order.order_number}.`
      : `vielen Dank für Ihre Bestellung. Anbei erhalten Sie Ihre Bestellbestätigung ${order.order_number}.`
    }

Gesamtbetrag: ${eur(order.total)}

${order.notes ? `Bemerkungen: ${order.notes}\n` : ''}

Bei Fragen stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen
${s.company_name}
${s.phone}
${s.email}
  `.trim();

  try {
    await base44.integrations.Core.SendEmail({
      to: order.lead_email,
      subject,
      body: emailBody,
      attachments: pdfContent ? [{
        filename: `${order.order_number}.pdf`,
        content: pdfContent,
      }] : [],
    });
  } catch (error) {
    console.error('Email send error:', error);
    throw error;
  }
}

/**
 * Download PDF — gemeinsamer Pfad mit allen anderen Dokumenten
 * (lib/pdfRenderer.downloadPdfFromHtml): Apple/Mobil über Druckdialog,
 * sonst html2pdf mit Seitenumbruch-Regeln und Canvas-sicherer Skalierung.
 */
export async function downloadOrderPDF(order, customSettings) {
  try {
    const settings = customSettings || await fetchPdfSettings();
    const htmlContent = generatePDFTemplate(order, settings);
    const docPrefix = order.order_type === 'Angebot' ? 'AN' : 'BS';
    const fileName = order.order_number || `${docPrefix}-${Date.now()}`;
    await downloadPdfFromHtml(htmlContent, fileName);
  } catch (err) {
    console.error('[PDF] Fehler bei der PDF Generierung:', err);
    toast.error('PDF konnte nicht erstellt werden: ' + (err?.message || 'unbekannter Fehler'));
  }
}

/**
 * Preview HTML
 */
export function getOrderPreviewHTML(order, customSettings) {
  return generatePDFTemplate(order, customSettings);
}

export default {
  generateOrderPDF,
  sendOrderEmail,
  downloadOrderPDF,
  getOrderPreviewHTML,
  clearPdfSettingsCache,
};
